import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import stateList from '../../../components/common/states';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const emptyGst = {
  gst_number: '',
  state: '',
  state_code: '',
  address: '',
  pincode: '',
  pan_no: '',
  cin_no: '',
  accountNo: '',
  ifscCode: '',
  bankName: '',
  bankAddress: '',
  accountHolderName: '',
  branch: '',
  bankstate: '',
  branchName: '',
  branchCode: '',
  email: '',
  phone: '',
};

const KribhcoForm = () => {
  const { id } = useParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    business_name: '',
    entity_type: '',
    registration_type: '',
    nature_of_business: '',
    state: '', // Added main state field
    state_code: '', // Added main state_code field
    ad_code: '',
    iec_code: '',
    fssai_no: '',
    gst_details: [ { ...emptyGst } ],
  });
  const [originalFormData, setOriginalFormData] = useState({
    business_name: '',
    entity_type: '',
    registration_type: '',
    nature_of_business: '',
    state: '', // Added main state field
    state_code: '', // Added main state_code field
    ad_code: '',
    iec_code: '',
    fssai_no: '',
    gst_details: [ { ...emptyGst } ],
  });
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch Kribhco data for editing
  useEffect(() => {
    if (isEditMode && id) {
      setInitialLoading(true);
      console.log('Fetching Kribhco with ID:', id);
      const fetchKribhco = async () => {
        try {
          const url = `${API_BASE_URL}/api/masters/kribhco-get/${id}`;
          console.log('Fetching from URL:', url);
          const res = await fetch(url, {
            credentials: 'include',
          });
          console.log('Response status:', res.status);
          if (!res.ok) {
            console.error('Response not ok:', res.status, res.statusText);
            throw new Error('Failed to fetch Kribhco');
          }
          const result = await res.json();
          console.log('Fetched Kribhco data:', result);
          if (result.data) {
            console.log('Setting form data:', result.data);
            
            // Handle GST details data
            let gstDetails = [];
            if (result.data.gst_details && Array.isArray(result.data.gst_details)) {
              gstDetails = result.data.gst_details.map(gst => ({
                gst_number: gst.gst_number ?? '',
                state: gst.state ?? '',
                state_code: gst.state_code ?? '',
                address: gst.address ?? '',
                pincode: gst.pincode ?? '',
                pan_no: gst.pan_no ?? '',
                cin_no: gst.cin_no ?? '',
                accountNo: gst.accountNo ?? '',
                ifscCode: gst.ifscCode ?? '',
                bankName: gst.bankName ?? '',
                bankAddress: gst.bankAddress ?? '',
                accountHolderName: gst.accountHolderName ?? '',
                branch: gst.branch ?? '',
                bankstate: gst.bankstate ?? '',
                branchName: gst.branchName ?? '',
                branchCode: gst.branchCode ?? '',
                email: gst.email ?? '',
                phone: gst.phone ?? ''
              }));
            }
            
            // If no GST details, add one empty GST detail
            if (gstDetails.length === 0) {
              gstDetails = [{ ...emptyGst }];
            }
            
            const newFormData = {
              business_name: result.data.business_name ?? '',
              entity_type: result.data.entity_type ?? '',
              registration_type: result.data.registration_type ?? '',
              nature_of_business: result.data.nature_of_business ?? '',
              state: result.data.state ?? '', // Added main state field
              state_code: result.data.state_code ?? '', // Added main state_code field
              ad_code: result.data.ad_code ?? '',
              iec_code: result.data.iec_code ?? '',
              fssai_no: result.data.fssai_no ?? '',
              gst_details: gstDetails
            };
            console.log('Setting new form data with GST details:', newFormData);
            setFormData(newFormData);
            // Store original values for read-only check - ensure we have a deep copy
            const originalData = JSON.parse(JSON.stringify(newFormData));
            setOriginalFormData(originalData);
          } else {
            toast.error('Kribhco not found');
            navigate('/kribhco');
          }
        } catch (error) {
          console.error('Error fetching Kribhco:', error);
          toast.error('Failed to load Kribhco data');
          navigate('/kribhco');
        } finally {
          setInitialLoading(false);
        }
      };
      fetchKribhco();
    }
  }, [id, isEditMode, navigate]);

  // Helper function to check if field is read-only
  const isReadOnly = (fieldName) => {
    if (!isEditMode) return false;
    const originalValue = originalFormData[fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  // Helper function to check if GST field is read-only
  const isGstReadOnly = (fieldName, gstIndex) => {
    if (!isEditMode) return false;
    const originalGstDetails = originalFormData.gst_details;
    if (!originalGstDetails || !originalGstDetails[gstIndex]) return false;
    const originalValue = originalGstDetails[gstIndex][fieldName];
    return originalValue && originalValue.toString().trim() !== '';
  };

  // Field limits based on schema
  const FIELD_LIMITS = {
    business_name: 255,
    entity_type: 255,
    registration_type: 255,
    nature_of_business: 255,
    state: 255, // Added main state field limit
    state_code: 10, // Added main state_code field limit
    ad_code: 45,
    iec_code: 45,
    fssai_no: 45,
  };
  const GST_FIELD_LIMITS = {
    gst_number: 50,
    state: 255,
    state_code: 10,
    address: 1000,
    pincode: 10,
    pan_no: 50,
    cin_no: 45,
    accountNo: 50,
    ifscCode: 20,
    bankName: 100,
    bankAddress: 1000,
    accountHolderName: 100,
    branch: 100,
    bankstate: 100,
    branchName: 100,
    branchCode: 50,
    email: 45,
    phone: 20,
  };

  const validateField = (name, value, limits) => {
    let error = '';
    const limit = limits[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace(/_/g, ' ')}`;
    }
    // Required fields (only business_name is required in main form)
    if (name === 'business_name' && (!value || value.trim() === '')) {
      error = 'Business name is required';
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, FIELD_LIMITS);
    if (error) toast.error(error);
  };

  const handleGstBlur = (idx, e) => {
    const { name, value } = e.target;
    const error = validateField(name, value, GST_FIELD_LIMITS);
    if (error) toast.error(`GST Row ${idx + 1}: ${error}`);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only fields
    if (isReadOnly(name)) {
      return;
    }
    
    // Handle main state selection - auto-populate state_code
    if (name === 'state') {
      const selectedState = stateList.find(s => s.name === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        state_code: selectedState ? selectedState.code : ''
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleGstChange = (idx, e) => {
    const { name, value } = e.target;
    
    // Don't allow changes to read-only GST fields
    if (isGstReadOnly(name, idx)) {
      return;
    }
    
    setFormData(prev => {
      const gst_details = prev.gst_details.map((gst, i) =>
        i === idx ? { ...gst, [name]: value } : gst
      );
      // If state changes, update state_code
      if (name === 'state') {
        const matched = stateList.find(s => s.name === value);
        gst_details[idx].state_code = matched ? matched.code : '';
      }
      return { ...prev, gst_details };
    });
  };

  const addGstRow = () => {
    setFormData(prev => ({ ...prev, gst_details: [ ...prev.gst_details, { ...emptyGst } ] }));
  };

  const removeGstRow = (idx) => {
    setFormData(prev => ({ ...prev, gst_details: prev.gst_details.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Validate main form fields
    for (const key in formData) {
      if (FIELD_LIMITS[key]) {
        const error = validateField(key, formData[key], FIELD_LIMITS);
        if (error) {
          toast.error(error);
          setLoading(false);
          return;
        }
      }
    }
    
    // Validate GST details
    for (let i = 0; i < formData.gst_details.length; i++) {
      const gst = formData.gst_details[i];
      for (const key in gst) {
        if (GST_FIELD_LIMITS[key]) {
          const error = validateField(key, gst[key], GST_FIELD_LIMITS);
          if (error) {
            toast.error(`GST Row ${i + 1}: ${error}`);
            setLoading(false);
            return;
          }
        }
      }
    }
    
    try {
      const url = isEditMode 
        ? `${API_BASE_URL}/api/masters/kribhco-put/${id}`
        : `${API_BASE_URL}/api/masters/kribhco-post`;
      
      console.log('=== SUBMITTING KRIBHCO DATA ===');
      console.log('URL:', url);
      console.log('Method:', isEditMode ? 'PUT' : 'POST');
      console.log('Form data being sent:', formData);
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(isEditMode ? 'Kribhco Updated Successfully!' : 'Kribhco Registered Successfully!');
        if (!isEditMode) {
          setFormData({
            business_name: '', entity_type: '', registration_type: '', nature_of_business: '', 
            state: '', state_code: '', ad_code: '', iec_code: '', fssai_no: '', gst_details: [ { ...emptyGst } ],
          });
        } else {
          navigate('/kribhco');
        }
      } else {
        toast.error(data.error || 'Something went wrong');
      }
    } catch (err) {
      toast.error('Server Error');
    }
    setLoading(false);
  };

  return (
    <div className="max-w-8xl mx-auto p-4 bg-white">
      <ToastContainer />
      {initialLoading ? (
        <div className="text-center py-8 text-blue-600">Loading...</div>
      ) : (
        <>
          <div className="flex justify-between items-center border-b pb-3 mb-5">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-medium text-gray-800">{isEditMode ? 'Edit Kribhco' : 'Kribhco Registration'}</h2>
              {isEditMode && (
                <div className="text-xs text-gray-600">
                  <span className="inline-block w-3 h-3 bg-gray-100 border border-gray-300 mr-1"></span>
                  Read-only fields
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate('/kribhco')}
              className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-3 py-1 text-xs rounded font-normal hover:bg-[#10a5e9] hover:text-white hover:border-[#10a5e9] transition"
            >
              <FaArrowLeft />
              Back to List
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 mb-1">Business Name</label>
                <input 
                  type="text" 
                  name="business_name" 
                  value={formData.business_name} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.business_name} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('business_name') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('business_name')}
                  required 
                  autoComplete="off" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Entity Type</label>
                <input 
                  type="text" 
                  name="entity_type" 
                  value={formData.entity_type} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.entity_type} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('entity_type') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('entity_type')}
                  autoComplete="off" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Registration Type</label>
                <input 
                  type="text" 
                  name="registration_type" 
                  value={formData.registration_type} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.registration_type} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('registration_type') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('registration_type')}
                  autoComplete="off" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">Nature of Business</label>
                <input 
                  type="text" 
                  name="nature_of_business" 
                  value={formData.nature_of_business} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.nature_of_business} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('nature_of_business') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('nature_of_business')}
                  autoComplete="off" 
                />
              </div>
              {/* Added State dropdown next to Nature of Business */}
              <div>
                <label className="block text-sm text-gray-700 mb-1">State</label>
                <select 
                  name="state" 
                  value={formData.state} 
                  onChange={handleChange} 
                  onBlur={handleBlur}
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('state') ? 'bg-gray-100' : 'bg-white'}`}
                  disabled={isReadOnly('state')}
                >
                  <option value="">-- Select State --</option>
                  {stateList.map((s, i) => (
                    <option key={i} value={s.name}>{s.name}</option>
                  ))}
                </select>
              </div>
              {/* Added State Code (read-only, auto-populated) */}
              <div>
                <label className="block text-sm text-gray-700 mb-1">State Code</label>
                <input 
                  type="text" 
                  name="state_code" 
                  value={formData.state_code} 
                  readOnly
                  maxLength={FIELD_LIMITS.state_code} 
                  className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-gray-100 focus:outline-none rounded-none" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">AD Code</label>
                <input 
                  type="text" 
                  name="ad_code" 
                  value={formData.ad_code} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.ad_code} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('ad_code') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('ad_code')}
                  autoComplete="off" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">IEC CODE</label>
                <input 
                  type="text" 
                  name="iec_code" 
                  value={formData.iec_code} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.iec_code} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('iec_code') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('iec_code')}
                  autoComplete="off" 
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700 mb-1">FSSAI No</label>
                <input 
                  type="text" 
                  name="fssai_no" 
                  value={formData.fssai_no} 
                  onChange={handleChange} 
                  onBlur={handleBlur} 
                  maxLength={FIELD_LIMITS.fssai_no} 
                  className={`w-full border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400 rounded-none ${isReadOnly('fssai_no') ? 'bg-gray-100' : 'bg-white'}`}
                  readOnly={isReadOnly('fssai_no')}
                  autoComplete="off" 
                />
              </div>
            </div>
            
            {/* Rest of the GST details section remains the same */}
            <div className="pt-4">
              <h3 className="text-md font-semibold mb-2">GST and Bank Details</h3>
              {formData.gst_details.map((gst, idx) => (
                <div key={idx} className="border p-3 mb-3 rounded bg-gray-50 relative">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* All GST detail fields remain exactly the same */}
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Account Number</label>
                      <input 
                        type="text" 
                        name="accountNo" 
                        value={gst.accountNo} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.accountNo} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('accountNo', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('accountNo', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">IFSC Code</label>
                      <input 
                        type="text" 
                        name="ifscCode" 
                        value={gst.ifscCode} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.ifscCode} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('ifscCode', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('ifscCode', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Bank Name</label>
                      <input 
                        type="text" 
                        name="bankName" 
                        value={gst.bankName} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.bankName} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('bankName', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('bankName', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Bank Address</label>
                      <input 
                        type="text" 
                        name="bankAddress" 
                        value={gst.bankAddress} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.bankAddress} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('bankAddress', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('bankAddress', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Account Holder</label>
                      <input 
                        type="text" 
                        name="accountHolderName" 
                        value={gst.accountHolderName} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.accountHolderName} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('accountHolderName', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('accountHolderName', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Branch</label>
                      <input 
                        type="text" 
                        name="branch" 
                        value={gst.branch} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.branch} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('branch', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('branch', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Bank State</label>
                      <select 
                        name="bankstate" 
                        value={gst.bankstate} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('bankstate', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        disabled={isGstReadOnly('bankstate', idx)}
                      >
                        <option value="">-- Select State --</option>
                        {stateList.map((s, i) => (
                          <option key={i} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Branch Name</label>
                      <input 
                        type="text" 
                        name="branchName" 
                        value={gst.branchName} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.branchName} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('branchName', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('branchName', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Branch Code</label>
                      <input 
                        type="text" 
                        name="branchCode" 
                        value={gst.branchCode} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.branchCode} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('branchCode', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('branchCode', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">GST Number</label>
                      <input 
                        type="text" 
                        name="gst_number" 
                        value={gst.gst_number} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.gst_number} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('gst_number', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('gst_number', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">State</label>
                      <select 
                        name="state" 
                        value={gst.state} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('state', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        disabled={isGstReadOnly('state', idx)}
                      >
                        <option value="">-- Select State --</option>
                        {stateList.map((s, i) => (
                          <option key={i} value={s.name}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">State Code</label>
                      <input 
                        type="text" 
                        name="state_code" 
                        value={gst.state_code} 
                        readOnly 
                        maxLength={GST_FIELD_LIMITS.state_code} 
                        className="w-full border border-gray-300 px-2 py-1 text-xs bg-gray-100 focus:outline-none rounded-none" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Address</label>
                      <input 
                        type="text" 
                        name="address" 
                        value={gst.address} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.address} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('address', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('address', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Pincode</label>
                      <input 
                        type="text" 
                        name="pincode" 
                        value={gst.pincode} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.pincode} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('pincode', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('pincode', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">PAN No</label>
                      <input 
                        type="text" 
                        name="pan_no" 
                        value={gst.pan_no} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.pan_no} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('pan_no', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('pan_no', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">CIN No</label>
                      <input 
                        type="text" 
                        name="cin_no" 
                        value={gst.cin_no} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.cin_no} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('cin_no', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('cin_no', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Email</label>
                      <input 
                        type="email" 
                        name="email" 
                        value={gst.email} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.email} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('email', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('email', idx)}
                        autoComplete="off" 
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-700 mb-1">Phone</label>
                      <input 
                        type="text" 
                        name="phone" 
                        value={gst.phone} 
                        onChange={e => handleGstChange(idx, e)} 
                        onBlur={e => handleGstBlur(idx, e)} 
                        maxLength={GST_FIELD_LIMITS.phone} 
                        className={`w-full border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:border-blue-400 rounded-none ${isGstReadOnly('phone', idx) ? 'bg-gray-100' : 'bg-white'}`}
                        readOnly={isGstReadOnly('phone', idx)}
                        autoComplete="off" 
                      />
                    </div>
                  </div>
                  {formData.gst_details.length > 1 && (
                    <button 
                      type="button" 
                      onClick={() => removeGstRow(idx)} 
                      className="absolute top-2 right-2 text-red-500 hover:text-red-700 text-xs flex items-center gap-1"
                    >
                      <FaTrash /> Remove
                    </button>
                  )}
                </div>
              ))}
              <button 
                type="button" 
                onClick={addGstRow} 
                className="flex items-center gap-1 bg-gray-200 text-gray-800 px-2 py-1 text-xs rounded hover:bg-gray-300 border border-gray-300 font-medium mb-2"
              >
                <FaPlus /> Add GST
              </button>
            </div>
            <div className="pt-2 flex items-center gap-3">
              <button 
                type="submit" 
                className="px-4 py-2 text-xs bg-[#10a5e9] text-white font-normal rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60" 
                style={{ minWidth: '110px' }} 
                disabled={loading}
              >
                {loading ? 'Saving...' : (isEditMode ? 'Update Kribhco' : 'Save Kribhco')}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default KribhcoForm;
